import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

dayjs.extend(utc)

const createUserMessage = (threadId, prompt) => ({
  threadId,
  threadMessageId: null,
  actor: 'user',
  message: prompt,
  data: {},
  isGenerating: false,
  generationStatus: null,
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

const createAgentPlaceholderMessage = (threadId, responseId) => ({
  threadId,
  threadMessageId: responseId,
  actor: 'agent',
  message: '',
  data: {},
  isGenerating: true,
  generationStatus: 'Thinking...',
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

// List all user threads
export const useListAIUserThreads = (options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, categories } = options

  return useQuery({
    queryKey: ['denali/ai-user-threads', userId, categories],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-ai-user-threads', {
        categories: categories || ['assistant'] // Default to assistant category
      })
      return data
    },
    enabled
  })
}

// Get a specific thread
export const useAIThread = (threadId, options = {}) => {
  const { enabled = true, refetchInterval = null, includeMessages = true, includeAttachments = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread', threadId, includeMessages, includeAttachments],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread', {
        threadId,
        categories: ['assistant'],
        includeMessages,
        includeAttachments
      })
      return data
    },
    enabled: enabled && !!threadId,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval
  })
}

// Get a specific thread
export const useRenameAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-rename-thread', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-thread', command)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

// Get a specific thread message
export const useAIThreadMessage = (threadId, threadMessageId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-message', threadId, threadMessageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
        threadId,
        threadMessageId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageId
  })
}

// Get a specific message by flexible parent type
export const useAiMessage = (parentInfo, messageId, options = {}) => {
  const { enabled = true } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled
  })
}

// Get message events by flexible parent type
export const useAiMessageEvents = (parentInfo, messageId, options = {}) => {
  const { enabled = true, refetchInterval } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message-events', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message-events', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled,
    refetchInterval
  })
}

// Get thread attachment
export const useAIThreadAttachment = (threadId, threadMessageAttachmentId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-attachment', threadId, threadMessageAttachmentId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-attachment', {
        threadId,
        threadMessageAttachmentId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageAttachmentId
  })
}

export const useListUserScheduledPrompts = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-user-scheduled-prompts'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-user-scheduled-prompts', {
      })
      return data
    },
    enabled: enabled
  })
}

export const useScheduledPromptById = (scheduleId, include, options = {}) => {
  const { enabled = true } = options
  const includeKey = include ? JSON.stringify(include) : null

  return useQuery({
    queryKey: ['denali/get-scheduled-prompt-by-id', scheduleId, includeKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-scheduled-prompt-by-id', {
        scheduleId,
        include
      })
      return data
    },
    enabled: enabled
  })
}

export const useCreateAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-prompt', scheduledPrompt)

      return data
    }
  })
}

export const useModifyAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledDigest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (digest) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-digest', digest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateAiScheduledDigest = () => {
  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-digest', scheduledDigest)

      return data
    }
  })
}

export const useModifyAiScheduledDigest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-digest', scheduledDigest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRunAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-run-scheduled-prompt', command)

      return data
    }
  })
}

export const useSearchAIUserThreads = (query = {}, options = {}) => {
  const { enabled = true } = options
  const queryKey = query ? JSON.stringify(query) : '{}'

  return useQuery({
    queryKey: ['denali/search-ai-user-threads', queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', query)
      return data
    },
    enabled: enabled
  })
}

export const useSearchAIUserThreadsInfinite = (query, options = {}) => {
  const { enabled = true, pageSize = 10 } = options
  const { searchString, orderBy } = query

  return useInfiniteQuery({
    queryKey: ['denali/search-ai-user-threads-infinite', searchString, orderBy],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', {
        searchString,
        orderBy,
        skip: pageParam,
        take: pageSize
      })
      return data
    },
    enabled,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than pageSize, we've reached the end
      if (!lastPage?.data || lastPage.data.length < pageSize) {
        return undefined
      }
      // Calculate next skip value
      return allPages.length * pageSize
    }
  })
}

export const useCountAiUserThreads = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/count-ai-user-threads'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'count-ai-user-threads', {})
      return data?.count
    },
    enabled: enabled
  })
}

export const useUserReadingList = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-user-reading-list'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-user-reading-list', {})
      return data
    },
    enabled: enabled
  })
}

// Invoke the Denali AI
export const useInvokeDenaliAssistant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prompt, threadId, context, options }) => {
      // Optimistically add user message
      if (threadId) {
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev) {
            return {
              thread: {
                threadId,
                messages: [createUserMessage(threadId, prompt)]
              }
            }
          }
          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages: [...prev.thread.messages, createUserMessage(threadId, prompt)]
            }
          }
        })
      }

      const request = {
        prompt,
        ...(threadId && { threadId }),
        ...(context && { context }),
        ...(options && { options })
      }

      const { data } = await postNamedCommand('denali', 'invoke-denali-assistant', request)

      return data
    },
    onSuccess: async (data, { threadId: originalThreadId }) => {
      const threadId = data.threadId || originalThreadId
      const newMessage = data.message

      queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
        if (!prev) {
          return {
            thread: {
              threadId,
              messages: [newMessage, createAgentPlaceholderMessage(threadId, data.responseId)]
            }
          }
        }

        // Replace placeholder user message with actual message if it exists
        const messages = [...(prev.thread.messages || [])]
        const placeholderIndex = messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
        if (placeholderIndex !== -1) {
          messages[placeholderIndex] = newMessage
        } else {
          messages.push(newMessage)
        }

        // Add agent placeholder message
        messages.push(createAgentPlaceholderMessage(threadId, data.responseId))

        return {
          ...prev,
          thread: {
            ...prev.thread,
            messages
          }
        }
      })

      // Also invalidate the threads list to pick up the new thread
      queryClient.invalidateQueries(['denali/ai-user-threads'])
    }
  })
}

// Poll for message generation status
export const useMessagePolling = (message) => {
  const queryClient = useQueryClient()
  const refetchInterval = message?.isGenerating ? 2000 : null

  return useQuery({
    queryKey: ['denali/ai-thread-message-poll', message?.threadId, message?.threadMessageId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
          threadId: message.threadId,
          threadMessageId: message.threadMessageId,
          categories: ['assistant'],
          includeAttachments: true
        })

        return data
      } catch (error) {
        // 204 or 404 means message is still being generated
        if (error.response?.status === 204 || error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!message?.isGenerating && !!message?.threadId && !!message?.threadMessageId,
    refetchInterval,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (!data?.message) return

      const newMessage = data.message
      queryClient.setQueryData(['denali/ai-thread', message.threadId, true, true], prev => {
        if (!prev) return prev

        const messages = [...prev.thread.messages]
        const idx = messages.findIndex(m => m.threadMessageId === message.threadMessageId)
        if (idx > -1) {
          messages[idx] = newMessage
        }

        return {
          ...prev,
          thread: {
            ...prev.thread,
            messages
          }
        }
      })
    }
  })
}

// Note: Threads are created automatically when the first message is sent
// This is just a placeholder for potential future use
export const useCreateAIThread = () => {
  return {
    mutateAsync: async ({ title }) => {
      // Threads are created server-side when invoking the assistant
      // This is just a placeholder that returns a temporary ID
      return {
        threadId: null,
        title: title || 'New Chat',
        createdDate: dayjs.utc().toISOString()
      }
    }
  }
}
